/**
 * What a planner would have to CHANGE to change a proposal — the governed parameter behind a
 * reason code, a binding code or a driver (client request of 2026-10-06, relayed by the owner).
 *
 * «Perché» already said what bound a line. It did not say where to go about it, and that was the
 * question every allocator in the room asked: *this output is driven by which parameter, and
 * where do I find it?* A code with no route to its parameter is a diagnosis with no prescription.
 *
 * ── WHAT THIS FILE IS AND IS NOT ────────────────────────────────────────────
 *
 * It is a **route**, not a claim about a value. Naming the parameter that governs a code says
 * which registry row the engine read; it does NOT say that row set this line's number. The
 * distinction is the whole of G1 in practice, and three codes make it bite:
 *
 *   • `presentation_minimum_units` is the shelf minimum **where the store master is silent**. A
 *     door whose master carries one is bound by the master, and raising the parameter moves
 *     nothing for that door. The parameter card says so; the link gets the planner to it.
 *   • `door_capacity_check_enabled` is a **switch**, and the number it gates is the door's own
 *     `headroom_units` from the store master (`allocation.constraints.capacity_ceiling`). A
 *     planner who expects the parameter to hold the ceiling will not find it there.
 *   • `pres_min_floor` does not mean the presentation minimum won. The engine's floor is
 *     `round_up_to_case(max(presentation minimum, one per core size, protection floor, minimum
 *     order units, minimum transport units))` (`allocation.engine._shippable_block`), so the
 *     code means *the line landed on the floor* and five parameters can be the floor. All five
 *     are offered, largest-wins, which is exactly why raising one of them sometimes does nothing.
 *
 * It is also **not a list of every parameter the run read**. A code maps to the parameters that
 * determine THAT term, in the order a planner should look at them.
 *
 * ── CODES WITH NO PARAMETER BEHIND THEM ─────────────────────────────────────
 *
 * Half the vocabulary is not a parameter at all, and saying so is the honest answer rather than
 * an omission: a measured fact (the DC pool, the stock ledger), a master-data row, an assortment
 * decision, the objective's own ranking, or — for `high_velocity_fill` — a threshold the platform
 * states that nobody has authored (`replenishment.reasons.UNREACHABLE_REASON_CODES`). Each gets a
 * sentence naming where it comes from, so a planner stops looking for a knob that is not there.
 *
 * ── WHY A MAP AND NOT AN ENDPOINT ───────────────────────────────────────────
 *
 * No read publishes "the parameters behind this code". This is the client's judgement about its
 * own engine, read out of `planning.replenishment.{pull,explain,reasons}` and
 * `planning.allocation.{engine,constraints,sizerun}` and recorded here, next to the two maps that
 * already turn the same codes into prose (`./api.ts`, `./explain.tsx`). It degrades the same way
 * they do: a code this file does not name renders no route rather than a plausible wrong one, and
 * the platform can add a code without a release (G5).
 *
 * The parameter keys are the registry's own, verbatim, and each resolves to a catalogue label
 * through `registry.parameterKey.<key with dots as underscores>.label` — the key the platform's
 * parameter catalogue publishes as its `description_key`. A key spelled wrong here renders as a
 * missing catalogue key, which the i18n coverage gate fails on, so the spelling is checked by the
 * build rather than by a reader.
 */

/** Where a code comes from, when it does not come from a parameter. */
export type Governance =
  /** One or more registry rows govern it. The first is the one to look at first. */
  | { readonly kind: 'parameters'; readonly keys: readonly string[] }
  /** It is not a parameter: a measured fact, a master row, an assortment call, the objective. */
  | { readonly kind: 'elsewhere'; readonly noteKey: string }
  /** A code this build does not name. Nothing is drawn — never a guess. */
  | { readonly kind: 'unknown' };

/**
 * The registry keys behind each code, verified against
 * `services/repositories/parameter_catalogue.py`.
 *
 * `cover_breach` and `forecast_rate` share a horizon: the cover horizon is review periods (weeks)
 * plus the door's lead time in whole days (`pull.cover_horizon`; the governed key is the DEFAULT
 * lead time, `inseason.pull.lead_time_days`, read only for a door the master gives none — owner
 * ruling 2026-10-14; the weeks key `inseason.pull.lead_time_periods` is retired), the expected
 * demand is the forecast's central quantile over it, and the buffer above it is the service level. They differ in which end a
 * planner is arguing with, so they differ in which key is offered first.
 */
const GOVERNED_BY: Readonly<Record<string, readonly string[]>> = {
  // ── the ask (`reason_code`) ────────────────────────────────────────────────
  cover_breach: [
    'inseason.pull.service_target',
    'inseason.pull.review_periods',
    'inseason.pull.lead_time_days',
    'inseason.pull.central_level',
  ],
  below_presentation_min: ['inseason.pull.presentation_minimum_units'],

  // ── what bound the answer (`bound_by`) ────────────────────────────────────
  // The floor is the LARGEST of these, rounded up to a case: see the file's note.
  pres_min_floor: [
    'inseason.pull.presentation_minimum_units',
    'inseason.pull.minimum_order_units',
    'inseason.pull.minimum_transport_units',
    'allocation.core_size_coverage',
    'inseason.pull.case_size_units',
  ],
  case_rounded: ['inseason.pull.case_size_units'],
  capacity_capped: ['allocation.door_capacity_check_enabled', 'allocation.maximum_units_per_cell'],
  capped_by_capacity: ['allocation.door_capacity_check_enabled', 'allocation.maximum_units_per_cell'],
  size_run_dropped: ['allocation.core_size_coverage'],
  // ADR 6188 (platform): pairs trimmed after the solve because they did not pay for the carton,
  // pallet or shipment they needed.
  capped_by_shipment_cost: [
    'inseason.pull.shipment_fixed_cost',
    'inseason.pull.carton_units',
    'inseason.pull.carton_cost',
    'inseason.pull.pallet_units',
    'inseason.pull.pallet_cost',
    'inseason.pull.logistics_cost_per_unit',
  ],

  // ── the drivers (ADR 6038) ────────────────────────────────────────────────
  forecast_rate: [
    'inseason.pull.central_level',
    'inseason.pull.review_periods',
    'inseason.pull.lead_time_days',
    'inseason.pull.forecast_model_version',
  ],
  cover_target: ['inseason.pull.service_target'],
  presentation_minimum: ['inseason.pull.presentation_minimum_units'],
  case_rounding: ['inseason.pull.case_size_units'],
  // ADR 6188 (platform): the pairs the transport cost took off the line.
  shipment_cost: [
    'inseason.pull.shipment_fixed_cost',
    'inseason.pull.carton_units',
    'inseason.pull.carton_cost',
    'inseason.pull.pallet_units',
    'inseason.pull.pallet_cost',
    'inseason.pull.logistics_cost_per_unit',
  ],
};

/**
 * The codes that are not parameters, each with the sentence that says what they ARE. Written as
 * catalogue keys, like everything else a planner reads.
 */
const ELSEWHERE: Readonly<Record<string, string>> = {
  // Measured by the run, not set.
  capped_by_dc_stock: 'inseason.governed.dcPool',
  dc_availability: 'inseason.governed.dcPool',
  on_hand: 'inseason.governed.ledger',
  in_transit: 'inseason.governed.ledger',
  position_not_reported: 'inseason.governed.notReported',
  // Decided elsewhere.
  not_ranged: 'inseason.governed.assortment',
  door_protected: 'inseason.governed.runRequest',
  reserve_carveout: 'inseason.governed.runRequest',
  withheld_for_chase: 'inseason.governed.runRequest',
  // The objective's own ranking: which door went short is the solve's answer, not a threshold.
  demand_rank: 'inseason.governed.objective',
  // Arithmetic, not a choice.
  whole_units: 'inseason.governed.wholeUnits',
  // The engine's change, whatever it was: the line's `bound_by` names which.
  allocation: 'inseason.governed.seeBoundBy',
  // The platform states this one is unreachable for want of a governed threshold.
  high_velocity_fill: 'inseason.governed.notAuthored',
};

/** What governs `code`, or that this build cannot say. */
export function governanceOf(code: string): Governance {
  const keys = GOVERNED_BY[code];
  if (keys !== undefined) return { kind: 'parameters', keys };
  const noteKey = ELSEWHERE[code];
  if (noteKey !== undefined) return { kind: 'elsewhere', noteKey };
  return { kind: 'unknown' };
}

/**
 * The catalogue key of a parameter's own label — the platform's `description_key` convention:
 * the registry key with its dots as underscores. Pure.
 */
export function parameterLabelKey(parameterKey: string): string {
  return `registry.parameterKey.${parameterKey.replaceAll('.', '_')}.label`;
}

/**
 * The link that opens the parameter register on one key.
 *
 * `?app=` is deliberately NOT set: the register derives the app from the key it is asked to
 * select (`foundations/parameters-v7/open-app.ts`), and a second implementation of that rule here
 * would be the G11 violation this repository is arranged to avoid — the feature boundary forbids
 * importing it, which is the same rule stated as a lint.
 */
export function parameterHref(parameterKey: string): string {
  return `/foundations/parameters?parameter=${encodeURIComponent(parameterKey)}`;
}
